import type { CalendarClient, Calendar as CalendarModel } from '@wtfalch/calendar/calendar';
import type { ChatClient } from '@wtfalch/chat';
import { Button, Dialog, Icon, Panes, Segmented, SegmentedOption, Select } from '@wtfalch/design';
import {
  type Draft,
  type DraftAttachment,
  type MailClient,
  type MailIdentity,
  splitQuoted,
} from '@wtfalch/email/mailbox';
import { useEffect, useRef, useState } from 'react';

import { toIsoDuration } from '../calendar/layout.ts';
import { pathForMail } from '../routes.ts';
import { AttachButton, AttachmentChips, useAttachments } from './Attachments.tsx';
import { ComposeAside } from './ComposeAside.tsx';
import { isAddress, parseAddresses } from './format.ts';
import { isColleague, RecipientChips } from './RecipientChips.tsx';
import { holdSeconds, isFuture, type SendWhen, sendAt } from './sendLater.ts';

/** What `ThreadPane`'s own expand button seeds the composer with -- the
 *  reply's recipients, subject, quoted original and text written so far, the
 *  thread headers that make it a reply, and the files already attached -- so
 *  switching to the full screen never loses what was already there. Every
 *  field is optional: the plain "Write" button (Today's, and Mail's own
 *  header) opens with nothing to seed. */
export type ComposeAnswering = { from: string; count: number };

export type ComposeInitial = {
  /** The thread a reply answers, named in the "While you write" column. */
  answering?: ComposeAnswering;
  to?: string;
  cc?: string;
  subject?: string;
  body?: string;
  inReplyTo?: readonly string[];
  references?: readonly string[];
  attachments?: readonly DraftAttachment[];
  /** The thread this reply answers, if any -- set alongside `answering` so
   *  an invitation made from it (agora#480) can link back to it. Absent for
   *  a plain "Write" with nothing to answer. */
  sourceMailboxId?: string;
  sourceThreadId?: string;
};

/** What one box can write (agora#217). */
export type ComposeKind = 'mail' | 'message' | 'invitation';

const KIND_LABELS: Record<ComposeKind, string> = {
  mail: 'Mail',
  message: 'Message',
  invitation: 'Invitation',
};
const KIND_ICONS = { mail: 'mail', message: 'chat', invitation: 'calendar' } as const;
const DURATIONS = [15, 30, 60] as const;

/**
 * The full write screen -- `Compose.dc.html`'s `compose()`. One box that
 * writes a mail, a chat message or a calendar invitation (agora#217): the
 * board's seg switch between the three sits in the box's own top row, and
 * each kind goes out through the send path that already existed for it --
 * `onSend` (Mail's own `sendMail`) for a mail, `ChatClient.sendMessage` for a
 * message, `CalendarClient.createEvent` with scheduling messages on for an
 * invitation. A kind whose client this section was not given is not drawn
 * in the switch, the way Chat or Calendar are left out of the rail.
 *
 * What was typed survives a switch where it means the same thing: the text
 * is the mail's body, the message's text and the invitation's description;
 * the address field and "About" carry between mail and invitation (the
 * invitation's title). A message goes to a room, picked from the joined
 * ones, so it has neither. The board's "While you write" aside is left out:
 * it needs data (an open thread it answers, an offered time) this section
 * does not have.
 *
 * The header reads "Write" while this is open (`Mail.tsx`'s own `onTitle`
 * lifts that into `AgoraShell`) -- this pane draws no second, in-pane title
 * of its own the way an earlier version did with a "New mail" bar.
 *
 * A token in an address field that is not an address is named under the
 * card and stops Send -- see `isAddress`.
 */
export function Compose({
  client,
  identities,
  initial,
  calendarClient,
  chatClient,
  agoraId,
  onSend,
  onCancel,
}: {
  client: MailClient;
  identities: readonly MailIdentity[];
  initial?: ComposeInitial;
  /** Draws the Invitation kind. */
  calendarClient?: CalendarClient;
  /** Draws the Message kind; `null` while Chat is off for this account. */
  chatClient?: ChatClient | null;
  /** For linking an invitation made from a reply back to its thread
   *  (agora#480, `sourceThreadOf`) -- absent only in a test that never
   *  reaches that path. */
  agoraId?: string;
  /** `delay`: seconds to hold the message for, when it is sent later. */
  onSend: (draft: Draft, options?: { delay?: number }) => Promise<void>;
  onCancel: () => void;
}) {
  const [identityId, setIdentityId] = useState(identities[0]?.id ?? '');
  const [to, setTo] = useState(initial?.to ?? '');
  const [ccBcc, setCcBcc] = useState(Boolean(initial?.cc));
  const [cc, setCc] = useState(initial?.cc ?? '');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState(initial?.subject ?? '');
  const [body, setBody] = useState(initial?.body ?? '');
  const [sending, setSending] = useState(false);
  const [kind, setKind] = useState<ComposeKind>('mail');
  const [roomId, setRoomId] = useState('');
  const [day, setDay] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState<(typeof DURATIONS)[number]>(30);
  const [calendars, setCalendars] = useState<readonly CalendarModel[] | null>(null);
  const [calendarsError, setCalendarsError] = useState('');
  const [sendError, setSendError] = useState('');
  const [discarding, setDiscarding] = useState(false);
  const [when, setWhen] = useState<SendWhen>('now');
  const [customAt, setCustomAt] = useState('');
  // One uid per draft, not per attempt: a retry after a createEvent that
  // landed on the server but failed on the way back replaces the event.
  const [eventUid] = useState(() => crypto.randomUUID());
  const attachments = useAttachments(client, initial?.attachments);
  const paneRef = useRef<HTMLDivElement>(null);
  const toRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  // Opening the composer lands the cursor where the writing starts: the
  // address, or the text when a reply already brought the address along.
  // biome-ignore lint/correctness/useExhaustiveDependencies: once, on open
  useEffect(() => {
    (initial?.to ? bodyRef : toRef).current?.focus();
  }, []);

  const kinds: ComposeKind[] = ['mail'];
  if (chatClient) kinds.push('message');
  if (calendarClient) kinds.push('invitation');
  const rooms = chatClient ? chatClient.rooms().filter((r) => r.membership === 'join') : [];
  const ownCalendar = calendars?.find(
    (c) => c.myRights.includes('mayWriteAll') || c.myRights.includes('mayWriteOwn'),
  );

  useEffect(() => {
    if (kind !== 'invitation' || !calendarClient) return;
    let live = true;
    setCalendarsError('');
    calendarClient
      .listCalendars()
      .then((list) => {
        if (live) setCalendars(list);
      })
      .catch((err: unknown) => {
        if (live) {
          setCalendars([]);
          setCalendarsError(err instanceof Error ? err.message : String(err));
        }
      });
    return () => {
      live = false;
    };
  }, [kind, calendarClient]);

  const identity = identities.find((i) => i.id === identityId) ?? identities[0] ?? null;
  const toAddresses = parseAddresses(to);
  // Only the fields on screen: Cc and Bcc belong to a mail, To to anything
  // but a chat message.
  const notAddresses = [
    ...(kind === 'message' ? [] : toAddresses),
    ...(kind === 'mail' && ccBcc ? [...parseAddresses(cc), ...parseAddresses(bcc)] : []),
  ]
    .map((a) => a.email)
    .filter((email) => !isAddress(email));
  const canSend =
    !sending &&
    notAddresses.length === 0 &&
    (kind === 'message'
      ? roomId !== '' && body.trim() !== ''
      : kind === 'invitation'
        ? Boolean(ownCalendar) &&
          toAddresses.length > 0 &&
          subject.trim() !== '' &&
          day !== '' &&
          time !== ''
        : Boolean(identity) && toAddresses.length > 0 && (subject.trim() || body.trim()));

  /** Why an invitation cannot be sent yet, when nothing else says so. */
  const invitationHint =
    kind !== 'invitation'
      ? ''
      : calendars === null
        ? 'Loading your calendars…'
        : calendarsError
          ? `Could not load your calendars: ${calendarsError}`
          : !ownCalendar
            ? 'You have no calendar you can add events to.'
            : '';
  const copied = cc.trim() !== '' || bcc.trim() !== '';
  const own = identity?.email ?? '';
  const chipped =
    kind === 'message'
      ? []
      : [
          ...toAddresses,
          ...(kind === 'mail' && ccBcc ? [...parseAddresses(cc), ...parseAddresses(bcc)] : []),
        ];
  const firstColleague =
    toAddresses.find((a) => isAddress(a.email) && isColleague(a.email, own)) ?? null;

  const send = async () => {
    if (!canSend) return;
    setSendError('');
    setSending(true);
    try {
      if (kind === 'message' && chatClient) {
        const joined = chatClient.rooms().filter((r) => r.membership === 'join');
        if (!joined.some((r) => r.id === roomId)) {
          setSendError('You are no longer in that room. Pick another.');
          return;
        }
        try {
          chatClient.sendMessage(roomId, body.trim());
        } catch (err) {
          setSendError(err instanceof Error ? err.message : String(err));
          return;
        }
        onCancel();
        return;
      }
      if (kind === 'invitation' && calendarClient && ownCalendar) {
        if (new Date(`${day}T${time}:00`).getTime() < Date.now()) {
          setSendError('That time has already passed. Pick a time to come.');
          return;
        }
        // Only the person's own words: a reply or forward's quoted original
        // is not the event's description.
        const description = splitQuoted(body).body.trim();
        // A link back to the thread this invitation answers (agora#480),
        // read by `sourceThreadOf` -- absent when this invitation did not
        // start from a reply, or when `agoraId` was not given.
        const links =
          agoraId && initial?.sourceMailboxId && initial?.sourceThreadId
            ? [
                {
                  href: `${window.location.origin}${pathForMail(agoraId, {
                    mailboxId: initial.sourceMailboxId,
                    threadId: initial.sourceThreadId,
                  })}`,
                  title: subject.trim() || undefined,
                },
              ]
            : undefined;
        try {
          await calendarClient.createEvent(
            ownCalendar.id,
            {
              calendarId: ownCalendar.id,
              uid: eventUid,
              title: subject.trim(),
              start: `${day}T${time}:00`,
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              duration: toIsoDuration(duration),
              isAllDay: false,
              status: 'confirmed',
              description: description || undefined,
              links,
              participants: toAddresses.map((a) => ({
                calendarAddress: a.email,
                roles: ['attendee' as const],
                participationStatus: 'needs-action' as const,
              })),
            },
            { sendSchedulingMessages: true },
          );
        } catch (err) {
          setSendError(err instanceof Error ? err.message : String(err));
          return;
        }
        onCancel();
        return;
      }
      if (!identity) return;
      let delay: number | undefined;
      if (when !== 'now') {
        const now = new Date();
        const target = sendAt(when, customAt, now);
        if (!target || !isFuture(target, now)) {
          setSendError('Pick a time at least a minute from now.');
          return;
        }
        delay = holdSeconds(target, now);
      }
      const draft: Draft = {
        identityId: identity.id,
        to: toAddresses,
        ...(cc.trim() ? { cc: parseAddresses(cc) } : {}),
        ...(bcc.trim() ? { bcc: parseAddresses(bcc) } : {}),
        subject: subject.trim(),
        text: body,
        ...(attachments.attachments.length ? { attachments: attachments.attachments } : {}),
        ...(initial?.inReplyTo ? { inReplyTo: initial.inReplyTo } : {}),
        ...(initial?.references ? { references: initial.references } : {}),
      };
      await (delay === undefined ? onSend(draft) : onSend(draft, { delay }));
    } catch (err) {
      setSendError(err instanceof Error ? err.message : String(err));
    } finally {
      setSending(false);
    }
  };

  /** The close button and Escape: a composer with something in it asks
   *  first, since closing it throws the text and the uploads away. */
  const askToClose = () => {
    if (to.trim() || body.trim() || attachments.attachments.length > 0) setDiscarding(true);
    else onCancel();
  };

  /** The canvas's own "Full screen" button -- there is no second, bigger
   *  screen for this pane to grow into inside the app (it already fills
   *  the card), so this asks the browser's own Fullscreen API for the
   *  nearest real thing rather than drawing a button that does nothing. */
  const toggleFullScreen = () => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void paneRef.current?.requestFullscreen();
    }
  };

  return (
    <Panes.Column flex className="mail-reader">
      {/* biome-ignore lint/a11y/noStaticElementInteractions: Escape is a shortcut for the close button, which stays the way in for the keyboard */}
      <div
        className="flex flex-1 flex-col min-w-0 min-h-0 overflow-hidden"
        ref={paneRef}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !e.defaultPrevented && !discarding) askToClose();
        }}
      >
        <div className="flex items-center gap-3 mail-compose-head">
          {kinds.length > 1 ? (
            <Segmented
              label="Write a"
              size="md"
              value={kind}
              onChange={(value) => {
                setSendError('');
                setKind(value as ComposeKind);
              }}
            >
              {kinds.map((k) => (
                <SegmentedOption key={k} value={k}>
                  <Icon name={KIND_ICONS[k]} size={15} />
                  {KIND_LABELS[k]}
                </SegmentedOption>
              ))}
            </Segmented>
          ) : null}
          <div className="grow min-w-0" />
          <Button
            kind="ghost"
            size="sm"
            iconOnly
            aria-label="Full screen"
            onPress={toggleFullScreen}
          >
            <Icon name="expand" size={18} />
          </Button>
          <Button kind="ghost" size="sm" iconOnly aria-label="Throw it away" onPress={askToClose}>
            <Icon name="close" size={18} />
          </Button>
        </div>
        <div className="flex flex-1 flex-col overflow-y-auto mail-compose-scroll">
          <div className="flex gap-5 mail-compose-layout">
            <div className="card mail-compose-card">
              {kind === 'message' ? (
                <div className="flex items-center gap-2 mail-compose-field">
                  <span className="text-xs text-muted flex-none w-10">In</span>
                  <Select
                    size="sm"
                    value={roomId}
                    onChange={(e) => setRoomId(e.target.value)}
                    aria-label="Room"
                    placeholder="Pick a room…"
                  >
                    {rooms.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </Select>
                </div>
              ) : (
                <div className="flex items-center gap-2 mail-compose-field">
                  <span className="text-xs text-muted flex-none w-10">To</span>
                  <input
                    ref={toRef}
                    className="mail-compose-input"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    placeholder="Somebody else…"
                    aria-label="To"
                  />
                  {/* Hiding the fields again is only offered while they are
                    empty: a hidden Cc would still be sent. */}
                  {kind === 'mail' && (!ccBcc || !copied) && (
                    <button
                      type="button"
                      className="lnk text-xs mail-compose-ccbcc"
                      onClick={() => setCcBcc(!ccBcc)}
                    >
                      {ccBcc ? 'Hide copy fields' : 'Copy, hidden copy'}
                    </button>
                  )}
                </div>
              )}
              {kind === 'mail' && ccBcc && (
                <>
                  <div className="flex items-center gap-2 mail-compose-field">
                    <span className="text-xs text-muted flex-none w-10">Cc</span>
                    <input
                      className="mail-compose-input"
                      value={cc}
                      onChange={(e) => setCc(e.target.value)}
                      aria-label="Cc"
                    />
                  </div>
                  <div className="flex items-center gap-2 mail-compose-field">
                    <span className="text-xs text-muted flex-none w-10">Bcc</span>
                    <input
                      className="mail-compose-input"
                      value={bcc}
                      onChange={(e) => setBcc(e.target.value)}
                      aria-label="Bcc"
                    />
                  </div>
                </>
              )}
              {chipped.length > 0 && own && (
                <div className="mail-compose-field">
                  <RecipientChips recipients={chipped} own={own} />
                </div>
              )}
              {kind !== 'message' && (
                <div className="flex items-center gap-2 mail-compose-field">
                  <span className="text-xs text-muted flex-none w-10">About</span>
                  <input
                    className="mail-compose-input font-strong"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    aria-label="About"
                  />
                </div>
              )}
              {kind === 'invitation' && (
                <div className="flex items-center gap-2 mail-compose-field mail-compose-when-row">
                  <span className="text-xs text-muted flex-none w-10">When</span>
                  <input
                    className="mail-compose-input mail-compose-when"
                    type="date"
                    value={day}
                    onChange={(e) => setDay(e.target.value)}
                    aria-label="Day"
                  />
                  <input
                    className="mail-compose-input mail-compose-when"
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    aria-label="Time"
                  />
                  <Segmented
                    label="How long"
                    size="sm"
                    value={String(duration)}
                    onChange={(value) => setDuration(Number(value) as (typeof DURATIONS)[number])}
                  >
                    {DURATIONS.map((d) => (
                      <SegmentedOption key={d} value={String(d)}>
                        {d < 60 ? `${d} min` : `${d / 60} hour`}
                      </SegmentedOption>
                    ))}
                  </Segmented>
                </div>
              )}
              {kind === 'invitation' && toAddresses.length > 0 && (
                <p className="text-xs text-muted mail-compose-field">
                  Invited: {toAddresses.map((a) => a.email).join(', ')}
                  {copied ? '. Cc and Bcc addresses will not be invited.' : ''}
                </p>
              )}
              <div className="mail-compose-body">
                <textarea
                  ref={bodyRef}
                  className="mail-compose-input resize-none grow"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                      e.preventDefault();
                      void send();
                    }
                  }}
                  placeholder={initial?.body ? 'Keep writing…' : 'Write your message…'}
                  aria-label="Text"
                />
                {kind === 'mail' && (
                  <AttachmentChips
                    attachments={attachments.attachments}
                    onRemove={attachments.remove}
                    uploading={attachments.busy}
                  />
                )}
              </div>
              {kind === 'mail' && identities.length > 0 && (
                <div className="flex items-center gap-2 mail-compose-sig">
                  <Icon name="pen" size={16} />
                  <span className="text-xs text-muted">From:</span>
                  <span className="text-xs text-muted grow min-w-0">
                    {identity ? `${identity.name || identity.email} · ${identity.email}` : ''}
                  </span>
                  {identities.length > 1 && (
                    <Select
                      size="sm"
                      value={identityId}
                      onChange={(e) => setIdentityId(e.target.value)}
                      aria-label="From"
                    >
                      {identities.map((i) => (
                        <option key={i.id} value={i.id}>
                          {i.email}
                        </option>
                      ))}
                    </Select>
                  )}
                </div>
              )}
              {(() => {
                const problem =
                  sendError ||
                  attachments.error ||
                  (notAddresses.length > 0 ? `Not an address: ${notAddresses.join(', ')}` : '');
                const shown = problem || invitationHint;
                return (
                  shown && (
                    <p
                      className={`text-xs mail-compose-field ${problem ? 'text-bad' : 'text-muted'}`}
                      role={problem ? 'alert' : 'status'}
                    >
                      {shown}
                    </p>
                  )
                );
              })()}
              <div className="flex items-center gap-3 mail-compose-foot">
                {kind === 'mail' && (
                  <AttachButton
                    onFiles={(files) => void attachments.add(files)}
                    disabled={attachments.busy || sending}
                  />
                )}
                <div className="grow min-w-0" />
                {kind === 'mail' && (
                  <>
                    {when === 'custom' && (
                      <input
                        className="mail-compose-input mail-compose-when"
                        type="datetime-local"
                        value={customAt}
                        onChange={(e) => setCustomAt(e.target.value)}
                        aria-label="Send at"
                      />
                    )}
                    <Select
                      size="sm"
                      value={when}
                      onChange={(e) => setWhen(e.target.value as SendWhen)}
                      aria-label="When to send"
                      icon={<Icon name="clock" size={15} />}
                    >
                      <option value="now">Send now</option>
                      <option value="evening">This evening, 18:00</option>
                      <option value="tomorrow">Tomorrow, 08:00</option>
                      <option value="custom">Pick a time…</option>
                    </Select>
                  </>
                )}
                <Button kind="primary" disabled={!canSend} onPress={() => void send()}>
                  <Icon name="send" size={18} />
                  <span>
                    {sending
                      ? 'Sending…'
                      : kind === 'mail' && when !== 'now'
                        ? 'Send later'
                        : 'Send'}
                  </span>
                </Button>
              </div>
            </div>
            {kind === 'mail' && (
              <ComposeAside
                answering={initial?.answering}
                calendarClient={calendarClient}
                colleague={firstColleague?.email ?? null}
                colleagueName={firstColleague?.name || firstColleague?.email || ''}
                onOfferTime={(label) =>
                  setBody(
                    (text) =>
                      `${text}${text && !text.endsWith('\n') ? '\n' : ''}Would ${label} suit you?\n`,
                  )
                }
              />
            )}
          </div>
        </div>
      </div>
      {discarding && (
        <Dialog
          title="Throw this away?"
          onCancel={() => setDiscarding(false)}
          actions={
            <>
              <Button kind="ghost" onPress={() => setDiscarding(false)}>
                Keep writing
              </Button>
              <Button onPress={onCancel}>Discard</Button>
            </>
          }
        >
          What you have written and attached here is lost.
        </Dialog>
      )}
    </Panes.Column>
  );
}
